import React, { useEffect, useRef } from 'react';

import {
  usePostFormState,
  usePostFormActions
} from './PostFormContext';
import PostRichTextEditor from './PostRichTextEditor';
import PostFormattingControls from './PostFormattingControls';
import Form from '../../Shared/UI/Form/components/Form';
import Field from '../../Shared/UI/Field/components/Field';
import FileInput from '../../Shared/UI/FileInput/components/FileInput';
import FormActions from '../../Shared/UI/FormActions/components/FormActions';
import Button from '../../Shared/UI/Button/components/Button';
import ResponsiveContainer from '../../Shared/UI/ResponsiveContainer/components/ResponsiveContainer';

import { setCursorPositionInDOM } from '../../RichTextEditor';
import { createPost, getUrlMetadata } from '../../Feed/components/restApi';

import editorStyles from './PostRichTextEditor.module.scss';
import styles from './PostForm.module.scss';

const PostForm = ({ handleCloseWizard }) => {
  const state = usePostFormState();
  const {
    initializeState,
    setEditorState,
    setPostAttachment,
    onResponse,
    onError,
  } = usePostFormActions();
  const urlDetectionTimer = useRef(null);

  useEffect(() => {
    initializeState();
  }, []);

  const documentManager = state?.postForm?.documentManager;
  const postAttachment = state?.postForm?.postAttachment;

  const editorState = documentManager ? { documentManager } : null;

  useEffect(() => {
    if (documentManager) {
      setCursorPositionInDOM({
        editableContentId: editorStyles.postEditorContainer,
        isibuteDocument: documentManager.getCurrentDocument(),
      });
    }
  }, [documentManager?.historyIndex]);

  useEffect(() => {
    if (documentManager) {
      detectUrlAndRetrieveMetadata();
    }
  }, [documentManager?.historyIndex]);

  const getPlainText = () => {
    if (!documentManager) return '';
    return documentManager.getCurrentDocument().plainText();
  };

  const getPlainTextLength = () => getPlainText().length;

  const handleSubmit = () => {
    const plainTextLength = getPlainTextLength();
    if (plainTextLength < 1 || plainTextLength > 1500) {
      return null;
    }

    createPost({
      onResponse,
      onError,
      body: {
        authenticityToken: document.querySelector("meta[name='csrf-token']").getAttribute("content"),
        plainTextMessage: getPlainText(),
        documentComponents: documentManager.getCurrentDocument().asJSON().documentComponents,
        attachementType: postAttachment?.type || null,
        attachedImage: postAttachment?.storableImageString || null,
        attachedImageFileName: postAttachment?.imageFileName || null,
        attachedWebpageUrl: postAttachment?.url || null,
        attachedWebpageImageUrl: postAttachment?.image || null,
      },
    })
  };

  const base64Image = (image) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const readerResult = reader.result;

      setPostAttachment({
        type: 'uploaded-image',
        imageFileName: image.name,
        previewableImageString: readerResult,
        storableImageString: readerResult.replace("data", "").replace(/^.+,/, ""),
      });
    };

    reader.readAsDataURL(image);
  };

  const isWithinCharacterLimit = () => getPlainTextLength() <= 1500;

  const getMessageHint = () => {
    return `${getPlainTextLength()}/1500 characters`;
  };

  const getMessageError = () => {
    const count = getPlainTextLength();

    if (!isWithinCharacterLimit()) {
      return `This post has ${count} characters, which exceeds the 1500 character limit. It will not successfully submit until you decrease the character count.`;
    }

    return undefined;
  };

  const detectUrlAndRetrieveMetadata = () => {
    if (urlDetectionTimer.current) {
      clearTimeout(urlDetectionTimer.current);
    }

    urlDetectionTimer.current = setTimeout(() => {
      const text = getPlainText();
      const urlRegexWithProtocol = /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)/g;
      const urlRegexWithoutProtocol = /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;

      if (urlRegexWithProtocol.test(text) || urlRegexWithoutProtocol.test(text)) {
        const protocolMatch = text.match(urlRegexWithProtocol) || [];
        const noProtocolMatch = text.match(urlRegexWithoutProtocol) || [];
        const detectedUrl = protocolMatch.concat(noProtocolMatch)[0];

        getUrlMetadata({
          detectedUrl,
          onResponse: (data) => {
            if (data.feedbackVariant === 'success') {
              setPostAttachment({
                type: 'url-metadata',
                url: data.url,
                image: data.image,
              });
            }
          },
          onError,
        });
      }
    }, 500);
  };

  if (!editorState) {
    return null;
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Field label="Upload Image" hint="Optional" id="uploadImage">
        <FileInput
          accept="image/png,image/gif,image/jpeg"
          onChange={(e) => { base64Image(e.target.files[0]) }}
        />
      </Field>

      {postAttachment && postAttachment.type == 'url-metadata' && (
        <div>
          <a href={postAttachment.url} target="_blank" rel="noopener noreferrer"><img className={styles.img} src={postAttachment.image} /></a>
        </div>
      )}

      {postAttachment && postAttachment.type == 'uploaded-image' && (
        <div>
          <img className={styles.img} src={postAttachment.previewableImageString} />
        </div>
      )}

      <Field label="Message" id="message" hint={getMessageHint()} error={getMessageError()}>
        <div>
          <PostFormattingControls
            contentEditable={true}
            setState={setEditorState}
            activeStyleTypes={state?.postForm?.activeStyleTypes}
            activeFormat={state?.postForm?.activeFormat}
            activeHref={state?.postForm?.activeHref}
            editableContentId={editorStyles.postEditorContainer}
          />
          <PostRichTextEditor
            state={editorState}
            setState={setEditorState}
            contentEditable={true}
          />
        </div>
      </Field>

      <ResponsiveContainer name="form-actions">
        <FormActions>
          <Button
            variant="secondary"
            onClick={() => {
              handleCloseWizard();
            }}
          >
            Cancel
          </Button>
          <Button type="submit">
            Create
          </Button>
        </FormActions>
      </ResponsiveContainer>
    </Form>
  );
};

export default PostForm;
